/**
 * Inbox Constants
 * Package type and status configurations.
 */

import {
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  ScaleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

import type { CorrespondencePackageType } from '../types';
// Re-export status configs from the canonical source
export {
  RENDER_STATUS_CONFIG,
  type StatusConfig,
  TRANSMISSION_STATUS_CONFIG,
} from '../utils/status';

// ============================================================================
// Package Type Configuration
// ============================================================================

export interface PackageTypeConfig {
  label: string;
  shortLabel: string;
  icon: typeof DocumentTextIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  dotColor: string;
}

export const PACKAGE_TYPE_CONFIG: Record<
  CorrespondencePackageType,
  PackageTypeConfig
> = {
  hipaa_authorization: {
    label: 'HIPAA Authorization',
    shortLabel: 'HIPAA',
    icon: ShieldCheckIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    dotColor: 'bg-blue-500',
  },
  foia_request: {
    label: 'FOIA Request',
    shortLabel: 'FOIA',
    icon: ClipboardDocumentListIcon,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    dotColor: 'bg-violet-500',
  },
  lien_notice: {
    label: 'Lien Notice',
    shortLabel: 'Lien',
    icon: EnvelopeIcon,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    dotColor: 'bg-amber-500',
  },
  demand_package: {
    label: 'Demand Letter',
    shortLabel: 'Demand',
    icon: ScaleIcon,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    dotColor: 'bg-rose-500',
  },
  follow_up: {
    label: 'Follow Up',
    shortLabel: 'Follow Up',
    icon: PaperAirplaneIcon,
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    dotColor: 'bg-sky-500',
  },
  subpoena: {
    label: 'Subpoena',
    shortLabel: 'Subpoena',
    icon: DocumentTextIcon,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-200',
    dotColor: 'bg-slate-500',
  },
  premises_lor: {
    label: 'Premises LOR',
    shortLabel: 'Premises LOR',
    icon: DocumentTextIcon,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    dotColor: 'bg-indigo-500',
  },
  custom: {
    label: 'Custom',
    shortLabel: 'Custom',
    icon: DocumentTextIcon,
    color: 'text-stone-600',
    bgColor: 'bg-stone-100',
    borderColor: 'border-stone-200',
    dotColor: 'bg-stone-500',
  },
  mini_tort_demand: {
    label: 'Mini-Tort Demand',
    shortLabel: 'Mini-Tort',
    icon: ScaleIcon,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    dotColor: 'bg-teal-500',
  },
};

// ============================================================================
// Filter Options
// ============================================================================

export const STATUS_FILTER_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Ready', value: 'ready' },
  { label: 'Sent', value: 'sent' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Failed', value: 'failed' },
] as const;

// ============================================================================
// Creatable Package Types
// ============================================================================

export const CREATABLE_PACKAGE_TYPES: CorrespondencePackageType[] = [
  'lien_notice',
  'custom',
];
